import React, { useState, useEffect } from 'react';
import './ScrollingGallery.css';
import { sectionFolders, getSectionIdsInDisplayOrder } from '../utils/imageLoader';

// Get R2 bucket URL from environment
const R2_BUCKET_URL = (process.env.REACT_APP_R2_BUCKET_URL || 'https://pub-83d62ce741a14038a0c2d68c529b6538.r2.dev').replace(/\/$/, '');

// Build cover image URLs from R2
// All cover images are named cover.jpg
const buildCoverUrl = (folderName) => {
  const path = `${folderName}/cover.jpg`.replace(/ /g, '%20');
  return `${R2_BUCKET_URL}/${path}`;
};

// Get section IDs in reverse order (so section 0 displays last)
const displayOrderSectionIds = getSectionIdsInDisplayOrder();

// Cover images loaded from R2 in display order (section 0 last)
const images = displayOrderSectionIds.map(sectionId => 
  buildCoverUrl(sectionFolders[sectionId])
);

const ScrollingGallery = ({ onNavigateToGallery }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextImageIndex, setNextImageIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [transitionProgress, setTransitionProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = scrollTop / docHeight;
      
      setScrollProgress(scrollPercent);
      
      // Calculate which image should be shown based on scroll position
      const exactIndex = scrollPercent * (images.length - 1);
      const currentIndex = Math.floor(exactIndex);
      const nextIndex = Math.min(currentIndex + 1, images.length - 1);
      const progress = exactIndex - currentIndex;
      
      setCurrentImageIndex(currentIndex);
      setNextImageIndex(nextIndex);
      setTransitionProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Create sections for scrolling - map display index to actual section ID
  const sections = images.map((image, displayIndex) => {
    const actualSectionId = displayOrderSectionIds[displayIndex];
    return {
      id: actualSectionId,
      title: getSectionTitle(actualSectionId),
      description: getSectionDescription(actualSectionId)
    };
  });

  // Section titles and descriptions - using actual section IDs
  function getSectionTitle(sectionId) {
    return sectionFolders[sectionId] || `Section ${sectionId + 1}`;
  }

  function getSectionDescription(sectionId) {
    const descriptions = {
      0: "",  // Europe 2025
      1: "",  // NYC Marathon 2024
      2: "",  // Japan 2024
      3: "",  // California 2024
      4: "",  // Key West 2024
      5: "",  // Mt. Rainier 2022
      6: ""   // Puerto Rico 2026
    };
    return descriptions[sectionId] || "";
  }

  return (
    <div className="scrolling-gallery">
      {/* Fixed background container */}
      <div className="background-container">
        {images.map((image, index) => {
          let opacity = 0;
          
          if (index === currentImageIndex) {
            opacity = 1 - transitionProgress;
          } else if (index === nextImageIndex && currentImageIndex !== nextImageIndex) {
            opacity = transitionProgress;
          } else if (currentImageIndex === nextImageIndex && index === currentImageIndex) {
            opacity = 1;
          }
          
          return (
            <div
              key={index}
              className="background-image"
              style={{ 
                backgroundImage: `url(${image})`,
                opacity: opacity
              }}
            />
          );
        })}
      </div>

      {/* Navigation */}
      <nav className="navigation">
        <div className="nav-content">
          <h1 className="logo">.</h1>
          <div className="nav-indicators">
            {images.map((_, index) => (
              <div
                key={index}
                className={`indicator ${
                  index === currentImageIndex ? 'active' : ''
                }`}
              />
            ))}
          </div>
        </div>
      </nav>

      {/* Scrollable content */}
      <div className="content">
        {sections.map((section, index) => (
          <section key={section.id} className="content-section">
            <div className="section-content">
              <h2 className="section-title">{section.title}</h2>
              <p className="section-description">{section.description}</p>
              
              <button 
                className="gallery-button"
                onClick={() => onNavigateToGallery(section.id)}
              >
                View Gallery
              </button>
              
              {index === 0 && (
                <div className="scroll-indicator">
                  <span>Scroll to explore</span>
                  <div className="scroll-arrow">↓</div>
                </div>
              )}
            </div>
          </section>
        ))}
      </div>

      {/* Progress indicator */}
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>
    </div>
  );
};

export default ScrollingGallery;
